/* eslint-disable react/destructuring-assignment */
import React from 'react';
import styled from 'styled-components';
import { space } from 'styled-system';
import { Classes, NumericInput as BlueprintNumericInput } from '@blueprintjs/core';

import { formAriaProps } from 'core/util/ariaUtils';
import { omitFieldAndAriaProps } from './util/omitFieldProps';
import shiftInputCursor from './util/shiftInputCursor';

const StyledNumericInput = styled(BlueprintNumericInput)`
  ${space};
  display: block;

  /* have to force the font-family here, because Blueprint does not allow this to be overridden with SCSS */
  .${Classes.INPUT} {
    font-family: ${({ theme }) => theme.fonts.body};
  }

  &.${Classes.LARGE} {
    /* force large to be the normal Blueprint sized inputs, since by default we use small */
    input {
      height: 30px;
      font-size: 14px;
    }

    .bp4-input:not(:first-child) {
      padding-left: 35px;
    }

    > .bp4-icon,
    .bp4-input-action > .bp4-spinner {
      margin: 7px;
    }

    .${Classes.TAG} {
      min-width: 20px;
      min-height: 20px;
      padding: 0 8px;
      margin: 5px;
    }

    .bp4-button {
      min-width: 24px;
      min-height: 24px;
      margin: 3px;
    }
  }

  &.large {
    > .bp4-icon,
    .bp4-input-action > .bp4-spinner {
      margin: 10px;
    }

    .bp4-input-action .bp4-button {
      font-size: 14px;
    }
  }

  &.bp4-large {
    .bp4-input-action .bp4-button {
      font-size: 14px;
    }
  }

  &.jumbo {
    input {
      height: 40px;
      font-size: 16px;
      padding-left: 10px;
    }

    .bp4-input:not(:first-child) {
      padding-left: 40px;
    }

    .bp4-input-action {
      height: 100%;
    }

    > .bp4-icon,
    .bp4-input-action > .bp4-spinner {
      margin: 12px;
    }

    .bp4-button {
      min-width: 30px;
      min-height: 30px;
      margin: 0;
    }
  }

  &.round {
    input {
      border-radius: 50px;
    }
  }

  &.chips {
    .bp4-input-left-container {
      left: 5px;
      height: 100%;
      display: flex;
      align-items: center;
      padding-left: 10px;
      padding-right: 10px;
    }
  }

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  // number input spinner arrows, be gone!
  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

const NumericInput = (props) => {
  const {
    buttonAriaLabel,
    ariaLabel,
    autoComplete,
    autoFocus,
    clearable,
    field,
    maxWidth,
    onQuery,
    onEnterKey,
    display,
    width,
    height,
    textAlign,
    rightElement,
    labelId,
    helperTextId,
    ...rest
  } = props;

  let { onFocus } = props;

  const handleOnValueChange = (value) => {
    // the only possible scenario we end up with NaN is when the user types in a flat '-'
    if (Number.isNaN(value)) {
      props.onChange?.('-');
    } else {
      props.onChange?.(value);
    }
  };

  if (autoFocus) {
    onFocus = (e) => {
      shiftInputCursor(e);

      if (props.onFocus) {
        props.onFocus(e);
      }
    };
  }

  const onBlur = (e) => {
    if (field) {
      field.setPristine(false);
    }

    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const onKeyDown = (e) => {
    const { onKeyDown: onKeyDownProp } = props;

    // filter out 'e' and other non-numeric inputs
    if (e.key === 'e' || e.key === 'E' || e.key === '+') {
      e.preventDefault();
      return;
    }

    if (onKeyDownProp) {
      onKeyDownProp(e);
    }
  };

  const dataAttributes = {};
  if (autoComplete === 'off') {
    dataAttributes['data-lpignore'] = 'true';
    dataAttributes.autoComplete = 'off';
  } else {
    dataAttributes.autoComplete = autoComplete;
  }

  const ariaProps = formAriaProps(
    props,
    { ariaRequired: rest.isRequired },
    {
      passOnExistingAria: true
    }
  );
  // Note inputRef is a prop in Blueprint and it could be coming from FieldState

  return (
    <StyledNumericInput
      {...ariaProps}
      {...omitFieldAndAriaProps(rest)}
      onKeyDown={onKeyDown}
      asyncControl
      autoFocus={autoFocus}
      onFocus={onFocus}
      onBlur={onBlur}
      onValueChange={handleOnValueChange}
      style={{ display, maxWidth, width, height, textAlign }}
      {...dataAttributes}
    />
  );
};

NumericInput.defaultProps = {
  buttonPosition: 'none'
};

export default NumericInput;
